import type { FeedbackRating } from 'components/FeedbackRatingIcon'
import type { ResolvedByValue } from 'components/FeedbackResolvedByCell'
import { api } from './index'

export type TransactionReference = {
  value: string
  type: 'authorization' | 'refund'
}

export type GiftCardTransaction = {
  id: string
  provider: string
  amount: number
  state: 'pending' | 'processed' | 'refunded'
  references: TransactionReference[]
  created_at: string
  updated_at: string
}

export type OrderModifier = {
  id: number
  item_modifier_id: number
  name: string
  quantity: number
  parent_item_modifier_id: number
}

export type OrderItem = {
  id: number
  item_id: number
  name: string
  quantity: number
  notes: string
  price: number
  modifiers: OrderModifier[]
}

// AI-1290 (FR-33): the rating a customer left on this order, or null when
// unrated. Mirrors merchant-api's OrderEntity#rating (nexus#1971) verbatim —
// `id` is the OrderRating HASHID (a raw integer id 404s on every
// rating-linked route), and `rating`/`resolved_by` reuse the exact same
// FeedbackRating/ResolvedByValue types the Order feedback tab and the Order
// feedback inbox already key their own rating rows on, so the three surfaces
// can't drift apart on what "ai" vs "operator" vs an unrated row means.
//
// `conversation_id` drives which detail screen a rated row links to, exactly as
// it does on api/orderRatings.types.ts's OrderRating: a conversation-backed
// rating opens the RecoveryAI case, one without opens the Reports rating
// detail. merchant-api loads the conversation anyway to decide resolved_by, so
// exposing the id costs no extra query.
export type OrderRating = {
  id: string
  rating: FeedbackRating | null
  comment: string | null
  resolved_by: ResolvedByValue
  conversation_id: string | null
}

export type Order = {
  id: number
  provider_uid: string
  provider_order_uid: string
  ordering_provider: string
  payment_type: string
  card_id: number
  card: {
    type: string
    last4: string
  }
  location_id: number
  location: {
    name: string
    street: string
    city: string
    state: string
    zip: string
  } | null
  placed_at: string
  completed_at: string
  status: string
  handoff: string
  price_breakdown: {
    subtotal: number
    total_discount: number
    total_tax: number
    total_fee: number
    tip: number
    total: number
  }
  gift_card_transactions?: GiftCardTransaction[]
  items?: OrderItem[]
  rating: OrderRating | null
}

export const ordersApi = api.injectEndpoints({
  endpoints: builder => ({
    getOrders: builder.query<
      { orders: Order[]; has_more_orders: boolean },
      { userId: number; includeItems: boolean }
    >({
      query: ({ userId, includeItems }) =>
        `orders?user_id=${userId}${includeItems ? '&include_items=true' : ''}`,
    }),
  }),
})
